/** @jsx jsx */

import { useContext, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext } from './interactionContext';
import { SelectOptionsContext } from './SelectOptionsRoot';
import setCurrentBlock from 'services/apiHelpers/setCurrentBlock';
import Select from 'react-select';
import { useSelector } from 'react-redux';
import { State } from 'store/store';
import EditMarker from 'components/UI/EditMarker';
import tick_mint from 'assets/tick_mint.svg';

const SelectOption = ({ children, ...props }) => {
  const iContext = useContext(interactionContext);

  const {
    dropDownTextColor = '#1D2026',
    dropDownBorder = true,
    dropDownBorderColor = '#19D1CF',
    dropDownBorderRadius = 10,
    dropDownBackgroundColor = 'transparent',
  } = iContext.props;

  const selectOptionsContext = useContext(SelectOptionsContext);

  const {
    selectedOptions,
    setSelectedOptions,
    correctAnswers,
  } = selectOptionsContext;

  const { id, type, index: selectGroupIndex, values } = props;

  const menuIsOpen = useSelector((state: State) => {
    if (!window.dev()) {
      return undefined;
    }
    return state.focusedNodeId === id;
  });

  const value = values[selectedOptions[id]];
  const [txt] = children;
  const nameId = `i${id}`;

  const _ = ({ innerRef, innerProps, ...props }) => {
    const { label, isFocused, data } = props;
    const { index } = data;

    return (
      <div
        ref={innerRef}
        {...innerProps}
        css={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          lineHeight: '1.1em',
          padding: '5px 10px',

          cursor: 'pointer',

          ...(window.dev()
            ? {
                color: dropDownTextColor,
              }
            : {
                color: dropDownTextColor,
                opacity: `${isFocused ? 0.8 : 1};`,
              }),
        })}
      >
        {label}
        {window.dev() && correctAnswers[selectGroupIndex] === index ? (
          <div
            css={css({
              width: 20,
              height: 20,
              borderRadius: '50%',
              background: `url('${tick_mint}') no-repeat`,
              backgroundSize: 20,
            })}
          />
        ) : null}
      </div>
    );
  };

  useEffect(() => {}, []);

  return (
    <div
      id={nameId}
      css={css({
        margin: '5px 0',
      })}
      onClick={
        window.dev()
          ? e => {
              setCurrentBlock(menuIsOpen ? null : { id });
            }
          : undefined
      }
    >
      <div
        css={css({
          flex: 1,
        })}
      >
        {txt}
      </div>

      <div style={{ height: 10 }} />

      <div
        css={css({
          position: 'relative',

          '[class*="control"]': {
            '*': {
              ':focus': {
                border: '0px !important',
              },
            },
          },

          '*': {
            fontSize: '16px !important',
            color: `${dropDownTextColor} !important`,

            ...(menuIsOpen && {
              '[class*="MenuList"]': {
                '*': {
                  pointerEvents: 'none',
                },
              },
            }),
          },
        })}
      >
        {/* @ts-ignore */}
        <Select
          key={value}
          menuIsOpen={menuIsOpen}
          isSearchable={false}
          styles={{
            control: (base, state) => {
              const { isFocused } = state;

              ['borderColor', 'boxShadow', '&:hover'].forEach(entry => {
                delete base[entry];
              });

              return {
                ...base,
                background: dropDownBackgroundColor,
                minHeight: 45,
                border: dropDownBorder
                  ? `1px solid ${dropDownBorderColor}`
                  : 'none',
                borderRadius: dropDownBorderRadius,
                cursor: 'pointer',
                ...(!window.dev() &&
                  isFocused && {
                    boxShadow: `0 0 0 2px ${dropDownBorderColor}`,
                  }),

                '[class*="placeholder"]': {
                  width: '97.5%',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  textOverflow: 'ellipsis',
                },

                '[class*="indicatorSeparator"]': {
                  display: 'none',
                },
              };
            },
            menu: (base, state) => ({
              ...base,
              borderRadius: 10 || dropDownBorderRadius,
              boxShadow: `0 23px 36px rgba(0, 0, 0, 0.2)`,

              background:
                dropDownBackgroundColor === 'transparent'
                  ? '#fff'
                  : dropDownBackgroundColor,
            }),
          }}
          placeholder={value}
          value={value}
          onChange={({ value }) => {
            setSelectedOptions(selectGroupIndex, values.indexOf(value));
          }}
          options={[
            ...values.map((value, index) => {
              return { value, label: value, index };
            }),
          ]}
          components={{ Option: _ }}
        />

        {window.dev() && (
          <div
            css={css({
              position: 'absolute',
              right: 0,
              top: 0,
              width: '100%',
              height: '100%',
            })}
          >
            <div
              css={css({
                position: 'absolute',
                left: -2,
                top: -2,
                width: 'calc(100% + 4px)',
                height: 'calc(100% + 4px)',
                boxShadow: menuIsOpen
                  ? '0 0 0 1px #ff0063'
                  : '0 0 0 1px lightGrey',
                cursor: 'pointer',
                transition: 'box-shadow 0.2s',

                ':hover': {
                  background: 'rgba(0,0,0,0.02)',
                },
              })}
            >
              {!menuIsOpen && <EditMarker />}
            </div>
          </div>
        )}
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
};

export default SelectOption;
